/**
 * Punch Action Registry
 *
 * Pluggable registry for punch clock actions. Domains register their actions
 * here (e.g., form_assignment) and the Punch Clock dynamically
 * renders the appropriate component based on the task's `task_actions` array.
 *
 * Mirrors the CRUD componentRegistry pattern.
 *
 * @example
 * // In domain punchActions.ts:
 * import { registerPunchAction } from '@ja/employee-portal/services/punchActionRegistry'
 *
 * registerPunchAction({
 *   key: 'form_assignment',
 *   component: () => import('@ja/forms/Components/PunchFormAction.vue'),
 *   trigger: 'while_clocked_in',
 *   blocking: false,
 *   defaultTiming: 'before',
 *   package: 'forms',
 * })
 */

import { shallowReactive, type AsyncComponentLoader, type Component } from 'vue'

import type { ScheduleStatus } from '../composables/usePunchClock'

// ============================================================================
// Types
// ============================================================================

export type PunchActionTrigger = 'on_clock_out' | 'while_clocked_in'

/** Timing relative to the checkbox tasks in the punch flow. */
export type PunchActionTiming = 'before' | 'after'

/** Shape of a task action as returned by the API (with optional timing override). */
export interface TaskActionEntry {
    key: string
    timing: PunchActionTiming | null
}

export interface PunchActionRegistration {
    /** Unique key matching the task_actions value (e.g., 'form_assignment') */
    key: string
    /** Lazy-loaded Vue component for the action UI */
    component: AsyncComponentLoader<Component>
    /** When this action activates */
    trigger: PunchActionTrigger
    /** If true and trigger='on_clock_out', clock-out cannot proceed until this action completes */
    blocking: boolean
    /** Default timing relative to checkbox tasks. PartyTask can override per action. */
    defaultTiming: PunchActionTiming
    /** Domain/package that registered this action */
    package: string
    /**
     * Optional admin-side detail view rendered inside the attendance PunchDetailSheet.
     * Must implement PunchActionAdminDetailProps. When omitted, attendance falls back
     * to a generic badge that uses `adminLabel` (or a humanized key).
     */
    adminDetailComponent?: AsyncComponentLoader<Component>
    /** Optional static label fallback for the generic admin badge when no component is supplied. */
    adminLabel?: string
}

/**
 * Contract every action component must follow.
 *
 * Props: scheduleIds, partyId, partyName, schedules
 * Emits: 'complete'(data: Record<string, unknown>) | 'cancel'
 */
export interface PunchActionComponentProps {
    scheduleIds: string[]
    partyId: string
    partyName: string
    schedules: ScheduleStatus[]
    /** When 'inline', the component should render without Teleport/fixed overlay */
    mode?: 'overlay' | 'inline'
    /** Restored state from IndexedDB session persistence (survives page refresh) */
    initialState?: Record<string, unknown>
}

/**
 * Contract every admin-side detail component must follow.
 *
 * Rendered inline inside the attendance PunchDetailSheet when a schedule has a
 * matching task_action entry. Components are read-only and receive the slice of
 * action_data captured for their key (or null if the employee hasn't completed
 * the action yet).
 */
export interface PunchActionAdminDetailProps {
    /** The registry key this component is rendering for (e.g. 'form_assignment'). */
    actionKey: string
    /** The schedule the task action belongs to. */
    scheduleId: string
    /**
     * The `action_data[actionKey]` slice from the first non-canceled punch
     * that has data for this key, or null when the action hasn't been completed.
     */
    actionData: Record<string, unknown> | null
    /** True if `actionData` is present and non-empty. */
    done: boolean
}

// ============================================================================
// Registry State
// ============================================================================

const registry = shallowReactive(new Map<string, PunchActionRegistration>())

// ============================================================================
// Core API
// ============================================================================

/**
 * Register a punch action.
 *
 * @param config - Action configuration (key, component, trigger, blocking, defaultTiming, package)
 */
export function registerPunchAction(config: PunchActionRegistration): void {
    if (registry.has(config.key)) {
        const existing = registry.get(config.key)
        console.warn(
            `[punchActionRegistry] Action '${config.key}' already registered by '${existing?.package}'. ` +
            `Overwriting with '${config.package}'.`,
        )
    }

    registry.set(config.key, config)

    if (import.meta.env.DEV) {
        console.log(`[punchActionRegistry] Registered '${config.key}' from '${config.package}' (${config.trigger}, blocking=${config.blocking})`)
    }
}

/**
 * Get a registered action by key.
 */
export function getPunchAction(key: string): PunchActionRegistration | undefined {
    return registry.get(key)
}

/**
 * Get all registered actions that match the given task_actions array and trigger type.
 */
export function getActionsForTrigger(
    taskActions: TaskActionEntry[],
    trigger: PunchActionTrigger,
): PunchActionRegistration[] {
    return taskActions
        .map(ta => registry.get(ta.key))
        .filter((reg): reg is PunchActionRegistration => reg !== undefined && reg.trigger === trigger)
}

/**
 * Resolve the effective timing for a task action.
 * PartyTask-level override takes precedence over registry default.
 */
export function resolveActionTiming(taskAction: TaskActionEntry): PunchActionTiming {
    if (taskAction.timing !== null) {
        return taskAction.timing
    }
    const reg = registry.get(taskAction.key)
    return reg?.defaultTiming ?? 'before'
}

/**
 * Check if any blocking on_clock_out actions exist for the given task_actions.
 */
export function hasBlockingActions(taskActions: TaskActionEntry[]): boolean {
    return taskActions.some(ta => {
        const reg = registry.get(ta.key)
        return reg !== undefined && reg.trigger === 'on_clock_out' && reg.blocking
    })
}

/**
 * Get all registered action keys.
 */
export function getRegisteredActions(): string[] {
    return Array.from(registry.keys())
}

/**
 * Check if an action is registered.
 */
export function hasPunchAction(key: string): boolean {
    return registry.has(key)
}

/**
 * Get the admin detail component loader for an action, if registered.
 */
export function getAdminDetailComponent(
    key: string,
): AsyncComponentLoader<Component> | undefined {
    return registry.get(key)?.adminDetailComponent
}

// ============================================================================
// Test Helpers
// ============================================================================

export function unregisterPunchAction(key: string): boolean {
    return registry.delete(key)
}

export function clearPunchActionRegistry(): void {
    registry.clear()
}
